import { useAuth } from '../context/AuthContext'
import { useGame } from '../context/GameContext'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Slider } from '../components/ui/Slider'
import { Flame, Sparkles, ArrowRight } from 'lucide-react'
import { format } from 'date-fns'
import { useState, useEffect } from 'react'
import { useToast } from '../components/ui/Toaster'

export default function DailyGame() {
  const { profile } = useAuth()
  const { dailyPrompt, loading, fetchDailyPrompt, submitDailyResponse } = useGame()
  const { toast } = useToast()
  const [spectrumA, setSpectrumA] = useState(50)
  const [spectrumB, setSpectrumB] = useState(50)
  const [showResult, setShowResult] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resultData, setResultData] = useState<{
    scoreA: number
    scoreB: number
    totalScore: number
    crowdAAvg: number
    crowdBAvg: number
  } | null>(null)

  const today = format(new Date(), 'EEEE, MMMM d')

  useEffect(() => {
    fetchDailyPrompt()
  }, [fetchDailyPrompt])

  useEffect(() => {
    if (dailyPrompt) {
      setSpectrumA(50)
      setSpectrumB(50)
      setShowResult(false)
      setResultData(null)
    }
  }, [dailyPrompt])

  const handleSubmit = async () => {
    if (!dailyPrompt) return
    
    setSubmitting(true)
    try {
      const crowdAAvg = dailyPrompt.crowd_a_avg ?? 50
      const crowdBAvg = dailyPrompt.crowd_b_avg ?? 50

      const { error } = await submitDailyResponse(spectrumA, spectrumB)
      
      if (error) throw error

      const scoreA = Math.max(0, 100 - Math.abs(spectrumA - crowdAAvg))
      const scoreB = Math.max(0, 100 - Math.abs(spectrumB - crowdBAvg))
      const totalScore = Math.round((scoreA + scoreB) / 2)

      setResultData({
        scoreA,
        scoreB,
        totalScore,
        crowdAAvg,
        crowdBAvg,
      })
      setShowResult(true)
      
      toast({
        type: 'success',
        title: 'Submitted!',
        message: `Your Overlap score: ${totalScore}`,
      })
    } catch (err) {
      toast({
        type: 'error',
        title: 'Failed to submit',
        message: err instanceof Error ? err.message : 'Please try again',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !dailyPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-surface-600 dark:text-surface-400">Loading today's prompt...</p>
        </div>
      </div>
    )
  }

  if (!dailyPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4">
        <Card className="max-w-md w-full text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-surface-400" />
          </div>
          <h2 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-2">No prompt today</h2>
          <p className="text-surface-500 dark:text-surface-400">Check back tomorrow for a new Overlap!</p>
        </Card>
      </div>
    )
  }

  const crowdAAvg = dailyPrompt.crowd_a_avg ?? 50
  const crowdBAvg = dailyPrompt.crowd_b_avg ?? 50

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 lg:py-10 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">Daily Overlap</p>
          <h1 className="text-3xl font-bold text-surface-900 dark:text-surface-100">{today}</h1>
        </div>
        {profile && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
              <Flame className="w-5 h-5" />
              <span>{profile.current_streak}</span>
              <span className="text-sm opacity-70">day streak</span>
            </div>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-6 space-y-8">
          {/* Spectrum A */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                {dailyPrompt.spectrum_a_label}
              </h3>
              {dailyPrompt.crowd_a_count > 0 && (
                <span className="text-sm text-surface-500 dark:text-surface-400">
                  {dailyPrompt.crowd_a_count.toLocaleString()} votes and avg: {crowdAAvg}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-sm text-surface-500 dark:text-surface-400 mb-3">
              <span>{dailyPrompt.spectrum_a_left}</span>
              <span>{dailyPrompt.spectrum_a_right}</span>
            </div>
            <Slider
              value={spectrumA}
              onChange={(val) => setSpectrumA(val)}
              min={0}
              max={100}
              step={1}
              leftLabel={dailyPrompt.spectrum_a_left}
              rightLabel={dailyPrompt.spectrum_a_right}
              disabled={showResult}
              showValue={true}
            />
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dashed border-surface-200 dark:border-surface-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white dark:bg-surface-900 px-3 text-surface-400">OR</span>
            </div>
          </div>

          {/* Spectrum B */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                {dailyPrompt.spectrum_b_label}
              </h3>
              {dailyPrompt.crowd_b_count > 0 && (
                <span className="text-sm text-surface-500 dark:text-surface-400">
                  {dailyPrompt.crowd_b_count.toLocaleString()} votes and avg: {crowdBAvg}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-sm text-surface-500 dark:text-surface-400 mb-3">
              <span>{dailyPrompt.spectrum_b_left}</span>
              <span>{dailyPrompt.spectrum_b_right}</span>
            </div>
            <Slider
              value={spectrumB}
              onChange={(val) => setSpectrumB(val)}
              min={0}
              max={100}
              step={1}
              leftLabel={dailyPrompt.spectrum_b_left}
              rightLabel={dailyPrompt.spectrum_b_right}
              disabled={showResult}
              showValue={true}
            />
          </div>

          {/* Submit / Result */}
          {showResult ? (
            <ResultCard
              scoreA={resultData!.scoreA}
              scoreB={resultData!.scoreB}
              totalScore={resultData!.totalScore}
              crowdAAvg={resultData!.crowdAAvg}
              crowdBAvg={resultData!.crowdBAvg}
              spectrumA={spectrumA}
              spectrumB={spectrumB}
              prompt={dailyPrompt}
            />
          ) : (
            <Button
              className="w-full"
              size="lg"
              onClick={handleSubmit}
              loading={submitting}
            >
              Submit & See Overlap <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

interface ResultCardProps {
  scoreA: number
  scoreB: number
  totalScore: number
  crowdAAvg: number
  crowdBAvg: number
  spectrumA: number
  spectrumB: number
  prompt: {
    spectrum_a_label: string
    spectrum_b_label: string
    spectrum_a_left: string
    spectrum_a_right: string
    spectrum_b_left: string
    spectrum_b_right: string
  }
}

function ResultCard({ scoreA, scoreB, totalScore, crowdAAvg, crowdBAvg, spectrumA, spectrumB, prompt }: ResultCardProps) {
  const vibeA = spectrumA > crowdAAvg + 15 ? 'weirdo' : spectrumA < crowdAAvg - 15 ? 'basic' : 'in sync'
  const vibeB = spectrumB > crowdBAvg + 15 ? 'weirdo' : spectrumB < crowdBAvg - 15 ? 'basic' : 'in sync'
  
  const vibeColors: Record<string, string> = {
    weirdo: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30',
    basic: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
    'in sync': 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
  }

  const vibeIcons: Record<string, React.ReactNode> = {
    weirdo: <Target className="w-3.5 h-3.5" />,
    basic: <Sparkles className="w-3.5 h-3.5" />,
    'in sync': <CheckCircle className="w-3.5 h-3.5" />,
  }

  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/30 rounded-xl border border-brand-200 dark:border-brand-800">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-brand-900 dark:text-brand-100">Your Overlap Score</h3>
        <span className="text-3xl font-black text-brand-600 dark:text-brand-400">{totalScore}</span>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white dark:bg-surface-900 rounded-xl p-4">
          <p className="text-sm font-medium text-surface-600 dark:text-surface-400">{prompt.spectrum_a_label}</p>
          <p className="text-2xl font-bold text-surface-900 dark:text-surface-100">{scoreA}</p>
          <p className="text-xs text-surface-500 dark:text-surface-500">
            You: {spectrumA} and Crowd: {crowdAAvg} and {vibeA}
          </p>
          <span className={clsx('inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-medium', vibeColors[vibeA])}>
            {vibeIcons[vibeA]}
            {vibeA.charAt(0).toUpperCase() + vibeA.slice(1)}
          </span>
        </div>
        <div className="bg-white dark:bg-surface-900 rounded-xl p-4">
          <p className="text-sm font-medium text-surface-600 dark:text-surface-400">{prompt.spectrum_b_label}</p>
          <p className="text-2xl font-bold text-surface-900 dark:text-surface-100">{scoreB}</p>
          <p className="text-xs text-surface-500 dark:text-surface-500">
            You: {spectrumB} and Crowd: {crowdBAvg} and {vibeB}
          </p>
          <span className={clsx('inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-medium', vibeColors[vibeB])}>
            {vibeIcons[vibeB]}
            {vibeB.charAt(0).toUpperCase() + vibeB.slice(1)}
          </span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button variant="secondary" className="flex-1" onClick={() => window.location.reload()}>
          Play Again Tomorrow
        </Button>
      </div>
    </div>
  )
}

import { Target, CheckCircle } from 'lucide-react'
import { clsx } from 'clsx'